import { useQuery } from "@tanstack/react-query";
import { apiGet, ApiError } from "@/lib/api/client";
import { useSession } from "@/features/auth/components/SessionProvider";
import { getActiveWorkspaceId } from "@/lib/api/workspace-context";

import type { WorkspacePlanTier } from "@/types/session";

export type { WorkspacePlanTier } from "@/types/session";

interface QuotaResponse {
  planTier: string;
}

export function useWorkspacePlanTier(): WorkspacePlanTier | null {
  const { token, profile, selectedContainer } = useSession();

  // Gate on profile and selectedContainer to ensure workspace context is established
  // (selectedContainer is set after containers are fetched, which happens after profile loads).
  // Without this gate, the query fires with 'default-workspace' sentinel and fails with 403.
  const canFetch = Boolean(token && profile && selectedContainer);
  const wsId = selectedContainer?.id ?? null;

  const { data, isLoading, isError } = useQuery<QuotaResponse | null>({
    // Scope by workspace id AND a distinct trailing segment so this query does
    // NOT share cache/state with ChatBox's ['workspace','quota'] usage poll. That
    // poll fires earlier (on `token` alone, before selectedContainer is set) and
    // can be in an error / wrong-workspace state — which previously leaked in here
    // as a transient `isError` and degraded a paid tier to 'free' (#3368).
    queryKey: ["workspace", wsId, "planTier"],
    queryFn: async () => {
      const id = wsId ?? getActiveWorkspaceId();
      const res = await apiGet<QuotaResponse>(`/workspaces/${id}/quota`);
      return res.data;
    },
    staleTime: 30_000,
    enabled: canFetch,
    // Don't retry on 4xx — they are permanent errors (e.g. 403 access denied).
    // The global retry config checks HttpError but apiGet throws ApiError,
    // so we must handle it here explicitly.
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        return false;
      }
      return failureCount < 1;
    },
  });

  // On 403 (workspace access denied) or other client errors, treat as "free"
  // so the paywall / access-denied flow can take over instead of spinning forever.
  if (isError) {
    return "free";
  }

  // Return null while loading so consumers can distinguish "unknown" from "free"
  if (isLoading && !data) return null;

  const tier = data?.planTier;
  // `trial` (PDR 011) is surfaced distinctly — it grants the free feature set but
  // the UI shows trial state, so do NOT collapse it to 'free' here.
  if (tier === "pro" || tier === "team" || tier === "trial") return tier;
  return "free";
}
